import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, User } from '../lib/supabase';
import { authService } from '../services/supabaseService';
import { getRedirectUrl } from '../utils/url';
import { debugLogForUser } from '../utils/asyncUtils';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => void;
  loading: boolean;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Starting authentication initialization');
        
        // Get the current session - Supabase will automatically restore from localStorage
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('AuthContext: Error getting session:', error);
          if (mounted) {
            setUser(null);
            setLoading(false);
            setInitializing(false);
          }
          return;
        }

        if (session?.user) {
          console.log('AuthContext: Found existing session, loading user profile');
          debugLogForUser(session.user.email || '', 'AuthContext:init:session_found', { 
            userId: session.user.id 
          });
          
          try {
            const profile = await authService.getCurrentUser(session);
            if (mounted && profile) {
              console.log('AuthContext: User profile loaded successfully');
              debugLogForUser(profile.email, 'AuthContext:init:profile_loaded', { 
                userId: profile.id,
                subscriptionStatus: profile.subscription_status 
              });
              setUser(profile);
            } else if (mounted) {
              console.log('AuthContext: Profile not found, creating minimal user');
              const minimalUser = {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
                subscription_tier: 'free',
                subscription_plan: 'free',
                subscription_status: 'inactive',
                weekly_time_allocation: 40,
                created_at: new Date().toISOString()
              };
              debugLogForUser(minimalUser.email, 'AuthContext:init:minimal_user_created');
              setUser(minimalUser);
            }
          } catch (profileError) {
            console.error('AuthContext: Error loading profile:', profileError);
            debugLogForUser(session.user.email || '', 'AuthContext:init:profile_error', profileError);
            
            if (mounted) {
              // Still set a minimal user to avoid login loops
              const fallbackUser = {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
                subscription_tier: 'free',
                subscription_plan: 'free',
                subscription_status: 'inactive',
                weekly_time_allocation: 40,
                created_at: new Date().toISOString()
              };
              debugLogForUser(fallbackUser.email, 'AuthContext:init:fallback_user_created');
              setUser(fallbackUser);
            }
          }
        } else {
          console.log('AuthContext: No existing session found');
          if (mounted) {
            setUser(null);
          }
        }
      } catch (error) {
        console.error('AuthContext: Error during initialization:', error);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setInitializing(false);
        }
      }
    };

    // Start initialization
    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted || initializing) return;

        console.log('AuthContext: Auth state change:', event);
        debugLogForUser(session?.user?.email || '', 'AuthContext:state_change', { 
          event, 
          hasSession: !!session,
          hasUser: !!session?.user 
        });

        try {
          if (event === 'SIGNED_OUT' || !session?.user) {
            console.log('AuthContext: User signed out');
            debugLogForUser('', 'AuthContext:state_change:signed_out');
            setUser(null);
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            console.log('AuthContext: User signed in or token refreshed');
            debugLogForUser(session.user.email || '', 'AuthContext:state_change:signed_in_or_refresh');
            
            try {
              const profile = await authService.getCurrentUser(session);
              if (profile) {
                console.log('AuthContext: Profile loaded from auth state change');
                debugLogForUser(profile.email, 'AuthContext:state_change:profile_loaded');
                setUser(profile);
              } else {
                // Fallback to minimal user
                const minimalUser = {
                  id: session.user.id,
                  email: session.user.email || '',
                  name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
                  subscription_tier: 'free',
                  subscription_plan: 'free',
                  subscription_status: 'inactive',
                  weekly_time_allocation: 40,
                  created_at: new Date().toISOString()
                };
                debugLogForUser(minimalUser.email, 'AuthContext:state_change:minimal_user_fallback');
                setUser(minimalUser);
              }
            } catch (profileError) {
              console.error('AuthContext: Error loading profile on auth change:', profileError);
              debugLogForUser(session.user.email || '', 'AuthContext:state_change:profile_error', profileError);
              
              // Still set minimal user to prevent auth loops
              const fallbackUser = {
                id: session.user.id,
                email: session.user.email || '',
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'User',
                subscription_tier: 'free',
                subscription_plan: 'free',
                subscription_status: 'inactive',
                weekly_time_allocation: 40,
                created_at: new Date().toISOString()
              };
              debugLogForUser(fallbackUser.email, 'AuthContext:state_change:fallback_user_created');
              setUser(fallbackUser);
            }
          }
        } catch (error) {
          console.error('AuthContext: Error in auth state change handler:', error);
          debugLogForUser(session?.user?.email || '', 'AuthContext:state_change:handler_error', error);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [initializing]);

  const login = async (email: string, password: string) => {
    try {
      console.log('🚀 AuthContext: Starting login process...');
      
      const result = await authService.signIn(email, password);
      
      if (result.user) {
        
        try {
          const profile = await authService.getCurrentUser(result.session);
          
          if (profile) {
            setUser(profile);
            console.log('✅ AuthContext: User state updated after login');
          }
        } catch (profileError) {
          console.error('🚨 AuthContext: Error fetching profile after login:', profileError);
          // Don't throw error here - let the auth state change handler deal with it
          // Just log the error and continue
        }
      } else {
        console.log('❌ AuthContext: Login result has no user');
      }
    } catch (error: any) {
      console.error('🚨 AuthContext: Login error:', error);
      console.error('🚨 AuthContext: Error details:', {
        message: error.message,
        status: error.status
      });
      
      // Ensure loading state is reset on error
      setLoading(false);
      throw new Error(error.message || 'Login failed');
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      console.log('AuthContext: Starting registration...');
      
      await authService.signUp(email, password, name);
      
      // Don't log the user in or fetch profile - they need to confirm email first
      console.log('AuthContext: Registration successful, awaiting email confirmation...');
      
    } catch (error: any) {
      console.error('AuthContext: Registration error:', error);
      // Ensure loading state is reset on error
      setLoading(false);
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      console.log('AuthContext: Logging out...');
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('AuthContext: Error signing out:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('🚀 AuthContext: Starting Google OAuth...');
      
      const redirectUrl = getRedirectUrl('/oauth-callback');
      
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      
      
      if (error) {
        throw error;
      }
      
      if (!data?.url) {
        throw new Error('No OAuth URL returned from Supabase');
      }
      
      // The redirect will happen automatically, so we don't need to do anything else here
      
    } catch (error: any) {
      
      // Provide more specific error messages
      if (error.message?.includes('provider')) {
        throw new Error('Google sign-in is not configured. Please contact support.');
      } else if (error.message?.includes('redirect')) {
        throw new Error('Invalid redirect configuration. Please try again later.');
      } else {
        throw new Error(error.message || 'Google sign-in failed. Please try again.');
      }
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      console.log('🔄 AuthContext: Updating user data locally:', userData);
      setUser({ ...user, ...userData });
    }
  };

  const value = {
    user,
    login,
    register,
    signInWithGoogle,
    logout,
    loading,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 